<template>
  <v-card outlined tile class="rounded-xl" style="height: 95%" v-if="selectedReviewItem">
    <v-overlay :value="loadingTakeControl || retrying">
      <div class="text-center">
        <div>
          <v-progress-circular
            class="my-4"
            :size="50"
            width="5"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>
        <p class="display-1">{{ retrying ? $t("Site.AI.RetryingRecognition") : $t("TakingControl") }}...</p>
      </div>
    </v-overlay>
    <div>
      <div class="d-flex align-center pl-3 pt-2 pb-3">
        <h4 class="flex-grow-1">{{ $t("InformationPendingReview") }}</h4>
       <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
            <v-btn
            v-bind="attrs"
            v-on="on"
            color="orange darken-1"
            outlined
            small
            @click="retryRecognition"
            :disabled="retrying || documentTakenByOther()"
            :loading="retrying"
            class="mr-3"
            >
            <v-icon small>mdi-refresh</v-icon>
            </v-btn>
        </template>
        <span>{{ documentTakenByOther() ? $t("WarningOfBlockedDocument") : $t("Site.AI.RetryRecognition") }}</span>
        </v-tooltip>

        <v-tooltip bottom v-if="!documentTakenByMe()">
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-btn
                color="light-green darken-3"
                disabled
              >
                {{ $t("ConfirmReview") }}
              </v-btn>
            </div>
          </template>
          <span>{{ $t("WarningOfBlockedDocument") }}</span>
        </v-tooltip>
        <v-btn
          v-else
          color="light-green darken-3"
          @click="save"
          :disabled="!documentTakenByMe()"
        >
          {{ $t("ConfirmReview") }}
        </v-btn>
      </div>
      <div
        style="
                display: flex;
                flex-direction: column;
                align-items: flex-start;
              "
        v-if="selectedReviewItem"
      >
        <div class="col-md-12 mb-1 pb-0">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
                    <span class="truncate" v-on="on">
                      {{ selectedReviewItem.originalFileName }}
                    </span>
            </template>
            <span>{{ selectedReviewItem.originalFileName }}</span>
          </v-tooltip>
        </div>
        <div class="grey--text col-md-12 mb-1 pt-0"
             v-if="selectedReviewItem && (selectedReviewItem.status === recognitionStatus.Failed || selectedReviewItem.status === recognitionStatus.PendingReview || selectedReviewItem.status === recognitionStatus.ContentTypeRecognitionFailed) && errorMessages.length">
          <v-icon
            class="mr-2"
            color="error"
            style="margin-left: auto"
            medium
          >
            mdi-alert-circle-outline
          </v-icon>
          <span class="red--text accent-4">{{ errorMessages[0] }}</span>
        </div>
      </div>
    </div>
    <div style="max-height: calc(75vh - 95px); overflow-y: auto">
      <v-container>
        <v-row justify="center" align="center" v-if="saving">
          <div class="text-center">
            <div>
              <v-progress-circular
                class="my-4"
                :size="50"
                width="5"
                color="primary"
                indeterminate
              ></v-progress-circular>
            </div>
            <p class="display-1">{{ $t("Loading") }}...</p>
          </div>
        </v-row>
        <div v-if="!saving">
          <v-col
            :md="12"
            v-for="(field, index) in document.fields"
            :key="`document-modal-field-${field.defFieldId}`"
            :error-messages="errorMessages"
            v-show="field.isVisible"
          >
            <DocumentField
              :ref="field.code"
              :index="index"
              :validator.sync="$v"
              :field="field"
              :documentId="document.id"
              :canAddData="false"
              :isNewValueFormat="true"
              :field-change-source="FieldChangeSources.DocumentIntelligence"
              :style="{
                      'background-image': isRecognisedField(field.code)
                        ? 'linear-gradient(180deg, #CEFAD0,rgba(255,0,0,0) ,rgba(255,0,0,0),rgba(255,0,0,0))'
                        : isNotRecognisedField(field.code)
                        ? 'linear-gradient(180deg, #FBF4D1,rgba(255,0,0,0) ,rgba(255,0,0,0),rgba(255,0,0,0))'
                        : '',
                    }"
              :documentFields="document.fields"
              color="#83f28f"
              @on-field-change="onFieldChange"
            />
            <DocumentPendingReviewOptionsValue
              v-if="field.optionsValue"
              :field="field"
              :index="index"
              @option-selected="onOptionSelected"
            />
          </v-col>
        </div>
      </v-container>
    </div>
  </v-card>
</template>
<script>
import DocumentField from '@/components/document/documentModal/field/DocumentField.vue';
import DocumentPendingReviewOptionsValue from '@/components/aiRecognition/Components/DocumentPendingReviewOptionsValue.vue';
import Utils from '@/ecm/common/Utils';
import DocumentService from '@/services/DocumentService';
import { validationMixin } from 'vuelidate';
import Document from '@/ecm/models/document/Document';
import recognitionStatus from '@/ecm/variables/RecognitionStatus';
import DocumentIntelligenceProvidersService from '@/services/DocumentIntelligenceProvidersService';
import RecognitionStatus from '@/ecm/variables/RecognitionStatus.js';
import { EventBus } from '@/eventBus';
import Attachment from '@/ecm/models/document/Attachment';
import BlockedDocumentsService from '@/ecm/models/contentType/documentIntelligence/BlockedDocumentsService.js';
import RemovePendingObjectSource from '@/ecm/variables/RemovePendingObjectSource.js';
import FieldValidations from '@/ecm/variables/FieldValidations';
import FieldType from '@/ecm/variables/FieldType';
import FieldChangeSources from '@/ecm/variables/FieldChangeSources';
import FailureOrigin from '@/ecm/variables/FailureOrigin';

export default {
    components: { DocumentField, DocumentPendingReviewOptionsValue },
    props: {
        selectedReviewItem: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            saving: false,
            loadingTakeControl: false,
            retrying: false,
            currentUser: this.$store.getters['account/user'],
            document: new Document(),
            structureCache: {},
            recognitionStatus: RecognitionStatus,
            errorMessages: [],
            blockedDocumentsService: new BlockedDocumentsService(this),
            createdDocumentsIds: [],
            showDocumentCreatedToast: false,
        };
    },

    mixins: [validationMixin],
    validations: {
        document: FieldValidations
    },

    computed: {
        FieldChangeSources() {
            return FieldChangeSources;
        },
        isRecognisedField() {
            return (code) => this.recognisedFields.includes(code);
        },
        isNotRecognisedField() {
            return (code) => this.notRecognisedFields.includes(code);
        }
    },
    mounted() {
        EventBus.$on('field-edited', this.fieldEdited);
    },
    destroyed() {
        EventBus.$off('field-edited');
    },
    methods: {
        async retryRecognition() {
            this.retrying = true;
            try {
                const messageId = this.selectedReviewItem.messageId;
                const originalFileName = this.selectedReviewItem.originalFileName;

                await this.takeControlOfDocument({
                    messageId: messageId,
                    originalFileName: originalFileName
                });

                const retryFailedRequest = {
                    PendingObjects: [this.selectedReviewItem]
                };

                const response = await DocumentIntelligenceProvidersService.sendPendingFailedObjectsToRequeue(retryFailedRequest);

                Utils.toast.success(response + ' ' + this.$t('DocumentsSuccesfullyRequeued'));

                await DocumentIntelligenceProvidersService.deleteLockedDocument({
                    messageId: messageId,
                    originalFileName: originalFileName
                });

                this.$emit('document-edited-without-save', false);
                this.$emit('document-requeued');
            } catch (error) {
                console.error('Error retrying recognition:', error);
                Utils.toast.error(this.$t('ErrorRetryingDocument'));
            } finally {
                this.retrying = false;
            }
        },
        async save() {
            this.saving = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                const mandatoryFieldErrors = this.validateMandatoryFields();

                if (mandatoryFieldErrors.length > 0) {
                    this.errorMessages.push(this.$t('MandatoryFieldValidationError').replace('{0}', mandatoryFieldErrors.join(', ')));
                }

                if (this.errorMessages.length === 0) {
                    // Generic error message:
                    this.errorMessages.push(this.$t('GenericFieldValidationError'));
                }

                this.saving = false;
                return;
            }

            try {
                await this.takeControlOfDocument({ messageId: this.selectedReviewItem.messageId, originalFileName: this.selectedReviewItem.originalFileName });

                const documentPendingObjectDTO = this.createDocumentPendingObjectDTO();
                const pendingObject = { status: this.selectedReviewItem.status, messageId: this.selectedReviewItem.messageId };
                const result = await DocumentService.saveDocumentPendingObject(documentPendingObjectDTO);

                await this.handleSaveSuccess(pendingObject, result);
            } catch (error) {
                console.error(error);
            } finally {
                this.saving = false;
            }
        },
        validateMandatoryFields() {
            const errors = [];
            this.document.fields.forEach((field, index) => {
                // Check if the 'required' field validation has failed
                if (!this.$v.document.fields.$each[index].value.required && field.isRequired) {
                    errors.push(field.title);
                }
            });

            return errors;
        },
        onFieldChange(field, source) {
            if (source !== FieldChangeSources.DocumentIntelligence) {
                return;
            }

            if (field.fieldsThatDependOn.length > 0) {
                this.$nextTick(() => {
                    this.refreshList(field.fieldsThatDependOn, field.isBeingCleared);
                    field.isBeingCleared = false;
                });
            }

            this.processFieldChange(field);
        },
        async onOptionSelected(option, field) {
            if (!this.documentTakenByMe()) {
                this.loadingTakeControl = true;
                await this.takeControlOfDocument({ messageId: this.selectedReviewItem.messageId, originalFileName: this.selectedReviewItem.originalFileName });
                this.loadingTakeControl = false;
            }

            if (field.fieldsThatDependOn.length > 0) {
                this.$nextTick(() => {
                    this.refreshList(field.fieldsThatDependOn);
                });
            }
            this.updateFieldValue(field, option);
            this.$emit('document-edited-without-save', true);
        },
        documentTakenByMe() {
            return this.selectedReviewItem.controlTakenByUserID === this.currentUser.Id;
        },
        documentTakenByOther() {
            return this.selectedReviewItem.controlTakenByUserID != null &&
                this.selectedReviewItem.controlTakenByUserID !== 0 &&
                this.selectedReviewItem.controlTakenByUserID !== this.currentUser.Id;
        },
        async loadContentTypeStructure(reloadDocFieldValidations = false) {
            this.recognisedFields = [];
            this.notRecognisedFields = [];
            let structureCt = null;
            if (this.structureCache[this.selectedReviewItem.contentTypeID]) {
                structureCt = this.structureCache[this.selectedReviewItem.contentTypeID];
            } else {
                structureCt = await DocumentService.getStructureIsoCompliance(
                    this.selectedReviewItem.contentTypeID,
                    []
                );

                structureCt.Fields.forEach((field) => {
                    if (field.Type === FieldType.DateTime && field.DefaultValue.includes('T')) {
                        field.DefaultValue = field.DefaultValue.split('T')[0];
                    }
                });

                this.selectFirstElementOnFieldsList(structureCt.Fields);
                this.structureCache[this.selectedReviewItem.contentTypeID] = structureCt;
            }
            this.document = new Document(structureCt);
            await this.loadPendingInformation(this.selectedReviewItem.messageId);

            if (reloadDocFieldValidations) {
                this.$v.$touch();
            }
        },
        selectFirstElementOnFieldsList(fields) {
            const fieldsWithRecognitionCodes = new Set(
                this.selectedReviewItem.fieldsConfig && this.selectedReviewItem.fieldsConfig.length > 0
                    ? this.selectedReviewItem.fieldsConfig.map(fc => fc.code)
                    : []
            );

            fields.forEach(field => {
                const fieldHasConfigRecognition = fieldsWithRecognitionCodes.has(field.Code);
                field.HasRecognitionConfig = fieldHasConfigRecognition;

                const isStaticList = field.Type === FieldType.StaticList;
                const hasListElements = Array.isArray(field.ListElements) && field.ListElements.length > 0;

                if (isStaticList && hasListElements && !fieldHasConfigRecognition) {
                    const firstElement = field.ListElements[0];
                    firstElement.Selected = true;
                    field.Value = firstElement.DisplayValue;
                }
            });
        },
        loadPendingInformation(messageId) {
            return DocumentIntelligenceProvidersService.getPendingValidationByMessageId(messageId)
                .then((response) => {
                    response.forEach((result) => {
                        const correspondingField = this.document.fields.find(
                            (field) => field.code === result.fieldCode
                        );
                        if (!correspondingField) {
                            console.warn(`Field with code ${result.fieldCode} not found`);
                            return;
                        }
                        correspondingField.isNewValueFormat = true;

                        if (result.results && result.results.length === 1) {
                            const innerResult = result.results[0];
                            this.updateFieldValue(correspondingField, innerResult);
                        } else if (result.results && result.results.length > 1) {
                            correspondingField.value = '';
                            correspondingField.optionsValue = [];

                            result.results.forEach((innerResult) => {
                                const customResult = {
                                    valueStr: innerResult.valueStr,
                                    isSelected: innerResult.isSelected,
                                    providerId: innerResult.documentIntelligenceProvider,
                                    valueDate: innerResult.valueDate,
                                    valueNum: innerResult.valueNum
                                };

                                if (correspondingField.type === FieldType.DateTime && innerResult.valueDate !== null) {
                                    customResult.valueStr = this.formatDate(innerResult.valueDate);
                                } else if (correspondingField.type === FieldType.Numeric && innerResult.valueNum !== null) {
                                    customResult.valueStr = this.numberToUserFormat(innerResult.valueNum);
                                }

                                correspondingField.optionsValue.push(customResult);
                            });
                        }
                    });
                })
                .catch((error) => {
                    console.error('Error in loadPendingInformation:', error);
                });
        },
        updateFieldValue(field, option) {
            if (field.type === FieldType.DateTime && option.valueDate !== null) {
                field.value = option.valueDate.toString().split('T')[0];
                field.valueDate = option.valueDate;
            } else if (field.type === FieldType.Numeric && option.valueNum !== null) {
                field.value = option.valueNum.toString();
                field.valueNumber = option.valueNum;
            } else if (option.valueStr !== null) {
                field.value = field.multiValue ? option.valueStr.split(',').map(val => val.trim()) : option.valueStr;
                field.valueString = option.valueStr;
            }
        },
        numberToUserFormat(number) {
            if (!number || isNaN(number)) return number;
            const inputDecSep = '.';
            const inputThouSep = '';
            const userDecSep = this.$store.getters['account/user'].MetricDecimalSep;
            const userThouSep = this.$store.getters['account/user'].MetricThousandSep;
            return Utils.number.changeFormat(number.toString(), inputDecSep, inputThouSep, userDecSep, userThouSep);
        },
        processFieldChange(field) {
            this.$emit('on-field-change', field);
        },

        fieldEdited(field, source) {
            if (source !== FieldChangeSources.DocumentIntelligence) {
                return;
            }

            this.$emit('document-edited-without-save', true);
            this.errorMessages = [];
            this.takeControlOfDocument({ messageId: this.selectedReviewItem.messageId, originalFileName: this.selectedReviewItem.originalFileName });
        },

        async takeControlOfDocument(takeControlRequestDTO) {
            const result = await this.blockedDocumentsService.checkDocumentAvailability(takeControlRequestDTO.messageId);

            // Document is being edited by me
            if (result != null && result.UserID === this.currentUser.Id) {
                return;
            }

            if (result != null && result.UserID !== this.currentUser.Id) {
                const optionResult = await this.blockedDocumentsService.confirmBlockAction(result);

                if (optionResult) {
                    this.loadingTakeControl = true;
                    DocumentIntelligenceProvidersService.takeControlOfDocument({ messageId: takeControlRequestDTO.messageId, originalFileName: takeControlRequestDTO.originalFileName }).then((result) => {
                        this.setControlTakenOptionForMessage(result.UserID);
                        this.loadingTakeControl = false;
                    });
                } else {
                    await this.loadContentTypeStructure();
                }

            } else {
                // Document is free to edit
                this.loadingTakeControl = true;
                DocumentIntelligenceProvidersService.takeControlOfDocument({
                    messageId: takeControlRequestDTO.messageId,
                    originalFileName: takeControlRequestDTO.originalFileName
                }).then((result) => {
                    this.setControlTakenOptionForMessage(result.UserID);
                    this.loadingTakeControl = false;
                });
            }
        },

        setControlTakenOptionForMessage(userID) {
            this.$emit('user-began-editing-a-document', this.selectedReviewItem, userID);
        },
        async handleSaveSuccess(pendingObject, documentIds) {
            await DocumentIntelligenceProvidersService.removeDocument(
                pendingObject.messageId,
                pendingObject.status,
                RemovePendingObjectSource.PendingObjectValidated
            );

            this.$emit('changes-have-been-saved', pendingObject.messageId, documentIds);
        },

        createDocumentPendingObjectDTO() {
            const documentDto = this.document.toDto(Utils.object.toJS(this.getAttachments()), true);
            documentDto.ObjectID = this.selectedReviewItem.objectId;
            return {
                Document: documentDto,
                PendingObj: {
                    MessageId: this.selectedReviewItem.messageId,
                    OrganizationID: this.selectedReviewItem.organizationID,
                    OriginalUserID: this.selectedReviewItem.originalUserID,
                    ContentTypeID: this.selectedReviewItem.contentTypeID,
                    Status: this.selectedReviewItem.status,
                    FilePath: this.selectedReviewItem.filePath,
                    Provider: this.selectedReviewItem.provider,
                    OriginalFileName: this.selectedReviewItem.originalFileName,
                    ObjectId: this.selectedReviewItem.objectId,
                    DocumentIdToLink: this.selectedReviewItem.documentIdToLink,
                    OriginalMessageId: this.selectedReviewItem.originalMessageId
                },
            };
        },
        getAttachments() {
            const attachments = [];

            const attachment = new Attachment();
            attachment.fileId = this.selectedReviewItem.messageId;
            attachment.fileName = this.selectedReviewItem.originalFileName;
            attachment.isLoading = true;
            attachment.extension = this.selectedReviewItem.filePath.split('.').pop();
            attachment.thumbnail = Utils.file.image.generateAvatar(
                attachment.extension,
                150
            );
            attachment.canDisplay = true;

            attachments.push(attachment);

            return attachments;
        },

        refreshList(fieldsToRefresh, isBeingCleared) {
            fieldsToRefresh.forEach((fieldCode) => {
                const fieldToRefresh = this.document.fields.filter((f) => f.code === fieldCode)[0];

                const parameters = [];

                fieldToRefresh.fieldsOnWhichDepends.forEach((paramCode) => {
                    let paramValue = this.document.fields.filter((f) => f.code === paramCode)[0].value;

                    let multiValue = false;
                    if (Array.isArray(paramValue)) {
                        paramValue = paramValue.filter((x) => x).join(',');
                        multiValue = true;
                    }

                    parameters.push({
                        Name: paramCode,
                        Value: paramValue || '',
                        Multivalue: multiValue,
                    });
                });
                EventBus.$emit('refreshListElements' + fieldToRefresh.code, parameters, isBeingCleared, FieldChangeSources.DocumentIntelligence);
            });
        },

        formatDate(dateString) {
            const userFormatDateWithTime = Utils.date.toUserFormatFromIso(dateString);
            const parts = userFormatDateWithTime.split(' ');
            return parts[0];
        },
    },
    watch: {
        selectedReviewItem(newVal, oldVal) {
            if (newVal && (!oldVal || newVal.messageId !== oldVal.messageId)) {
                if (newVal.status === recognitionStatus.PendingReview || newVal.status === recognitionStatus.Failed || newVal.status === recognitionStatus.ContentTypeRecognitionFailed) {
                    this.errorMessages = [];

                    if (newVal.errorData && newVal.errorData !== null) {
                        this.errorMessages.push(
                            this.$t(newVal.errorMessage).replace('{0}', `${newVal.errorData ? newVal.errorData : ''}`)
                        );
                    }

                    this.errorMessages.push(this.$t(newVal.errorMessage));
                }
                this.$v.$reset();

                if (newVal.contentTypeID == 0 || newVal.failureOrigin === FailureOrigin.MissingDocECMAutoImportConfig) {
                    this.document.fields = [];
                    return;
                }

                this.loadContentTypeStructure(true);
            }
        }
    },
};

</script>
